/**
 * Custom analytics event stream.
 *
 * `trackEvent(name, meta?)` fires a POST to /api/analytics/event with the
 * user's session id, UTM params (parsed once from the initial URL and stashed
 * in localStorage), and any per-event metadata. Never throws — analytics
 * failures must not break the user journey.
 *
 * A tiny in-memory de-dupe stops the same `name` from firing more than once
 * per 5-second window (e.g. React strict-mode double-effects).
 */
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "cap_analytics_session";
const UTM_KEY = "cap_analytics_utm";
const DEDUPE_MS = 5000;

const recentFires = new Map(); // name → last-fire timestamp

function _uuid() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* noop */ }
  return "s-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function _getSessionId() {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) { id = _uuid(); localStorage.setItem(SESSION_KEY, id); }
    return id;
  } catch { return _uuid(); }
}

/** Persist UTM params on first visit so downstream events keep attribution. */
export function captureUtmFromUrl() {
  try {
    const p = new URLSearchParams(window.location.search);
    const src = p.get("utm_source");
    const med = p.get("utm_medium");
    const cmp = p.get("utm_campaign");
    if (!src && !med && !cmp) return;
    const stored = _readUtm();
    // First-touch attribution — don't overwrite once set.
    if (stored?.utm_source) return;
    localStorage.setItem(UTM_KEY, JSON.stringify({
      utm_source: src || undefined,
      utm_medium: med || undefined,
      utm_campaign: cmp || undefined,
      captured_at: new Date().toISOString(),
    }));
  } catch { /* noop */ }
}

function _readUtm() {
  try { return JSON.parse(localStorage.getItem(UTM_KEY) || "null"); } catch { return null; }
}

/**
 * Fire a named analytics event. Fire-and-forget — the returned promise
 * resolves regardless of network outcome so callers can just call
 * `trackEvent("...")` without awaiting.
 */
export function trackEvent(name, meta) {
  if (!name) return Promise.resolve();
  const now = Date.now();
  const last = recentFires.get(name);
  if (last && now - last < DEDUPE_MS) return Promise.resolve();
  recentFires.set(name, now);
  const utm = _readUtm() || {};
  const body = {
    name,
    session_id: _getSessionId(),
    utm_source: utm.utm_source,
    utm_medium: utm.utm_medium,
    utm_campaign: utm.utm_campaign,
    meta: meta || undefined,
  };
  return axios.post(`${API}/analytics/event`, body, { timeout: 4000 })
    .then(() => undefined)
    .catch(() => undefined);
}

/** React hook helper — fire an event exactly once per component mount. */
export function useTrackOnMount(name, meta) {
  // Lazy import to avoid pulling react into this file's public surface.
  const React = require("react");
  React.useEffect(() => { trackEvent(name, meta); /* eslint-disable-next-line */ }, []);
}
